import { UseQueryResult } from "@tanstack/react-query";
import { getBuildingId } from "@/lib";
import {
  BookingAdminEvent,
  CalendarEvent,
  DateString,
  MeetingRoom,
  MeetingRoomBooking,
  MeetingRoomFeature,
  MultiFieldInvoiceReference,
  Timestamp,
} from "@/types";
import { debounceAsync, firebaseTimestampToDate } from "@/lib";
import client from "../client";
import { getImageFunction } from "./misc";
import { AxiosResponse } from "axios";
import { useBuildingQuery } from "@/components/RouterSetup";

const addSignedImageUrlsToPayload = async (
  rooms: MeetingRoom[],
): Promise<MeetingRoom[]> => {
  try {
    return await Promise.all(
      rooms.map(async (room) => ({
        ...room,
        thumbnail: room.thumbnail ? await getImageFunction(room.thumbnail) : "",
        images: room.images?.length
          ? await Promise.all(room.images.map(getImageFunction))
          : [],
      })),
    );
  } catch {
    return rooms;
  }
};

export const useMeetingRooms = (
  bookingEnabled = false,
  buildingAdminMode = false,
): UseQueryResult<MeetingRoom[]> => {
  return useBuildingQuery({
    queryKey: ["meeting-rooms", buildingAdminMode],
    queryFn: async () => {
      try {
        const response = await client.get(
          `/getAllMeetingRooms${buildingAdminMode ? "?includeReminderMessage=true" : ""}`,
        );
        if (!Array.isArray(response.data.data.rooms))
          throw new Error("Response is not an array");
        return await addSignedImageUrlsToPayload(
          response.data.data.rooms as MeetingRoom[],
        );
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: bookingEnabled,
  });
};

interface MeetingRoomSearchParams {
  /**
   * UTC datetime string in the format yyyy-MM-dd'T'HH:mm:ss'Z'
   */
  dateTime: string;
  /**
   * Duration should be provided in minutes
   */
  duration: number;
  capacity?: number;
  features?: MeetingRoomFeature[];
}
const fetchMeetingRooms = async ({
  dateTime,
  duration,
  capacity,
  features,
}: MeetingRoomSearchParams): Promise<MeetingRoom[]> => {
  try {
    const response = await client.get(
      `/getAvailableMeetingRooms?dateTime=${encodeURIComponent(dateTime)}&duration=${duration}${capacity ? "&capacity=" + capacity : ""}${features?.length ? "&features=" + features.map((f) => f.value).join(",") : ""}`,
    );
    if (!Array.isArray(response.data.data.rooms))
      throw new Error("Response is not an array");
    return await addSignedImageUrlsToPayload(
      response.data.data.rooms as MeetingRoom[],
    );
  } catch (error) {
    console.error(error);
    return [];
  }
};

const debouncedFetchMeetingRooms = debounceAsync(fetchMeetingRooms, 300);

export const useMeetingRoomSearch = (
  { dateTime, duration, capacity, features }: MeetingRoomSearchParams,
  bookingEnabled = false,
): UseQueryResult<MeetingRoom[]> => {
  return useBuildingQuery({
    queryKey: ["meeting-room-search", dateTime, duration, capacity, features],
    queryFn: async () =>
      debouncedFetchMeetingRooms({
        dateTime,
        duration,
        capacity,
        features,
      }),
    enabled: bookingEnabled,
  });
};

interface UseBookedSlotsProps {
  roomId: string;
}
/** The backend marks the logged-in user's own bookings via `isUsersBooking`. */
export const useBookedSlots = (
  { roomId }: UseBookedSlotsProps,
  bookingEnabled = false,
): UseQueryResult<CalendarEvent[]> => {
  return useBuildingQuery({
    queryKey: ["booked-slots", roomId],
    queryFn: async () => {
      try {
        const response = await client.get(`/getBookedSlots?roomId=${roomId}`);
        if (!Array.isArray(response.data.data.bookedSlots))
          throw new Error("Response is not an array");
        return response.data.data.bookedSlots.map((booking: CalendarEvent) => ({
          ...booking,
          start: firebaseTimestampToDate(booking.start as unknown as Timestamp),
          end: firebaseTimestampToDate(booking.end as unknown as Timestamp),
        }));
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: bookingEnabled && !!roomId,
  });
};

export interface UseAdminBookingsProps {
  roomId?: string;
  timeframe?: string | { from: DateString; to: DateString };
  status?: string;
  companyId?: string;
}

const processAdminBookings = (
  response: AxiosResponse<{ data: BookingAdminEvent[] }>,
) => {
  if (!Array.isArray(response.data.data))
    throw new Error("Response is not an array");
  return response.data.data.map((booking) => ({
    ...booking,
    createdTime: new Date(booking.createdTime as unknown as string),
    lastEditTime: new Date(booking.lastEditTime as unknown as string),
    start: new Date(booking.start as unknown as string),
    end: new Date(booking.end as unknown as string),
  }));
};

export const useBuildingBookings = (
  { roomId = "", timeframe = "", status = "" }: UseAdminBookingsProps,
  bookingEnabled = false,
): UseQueryResult<BookingAdminEvent[]> => {
  return useBuildingQuery({
    queryKey: ["building-bookings", roomId, timeframe, status],
    queryFn: async () => {
      try {
        const timeFilterString =
          typeof timeframe === "object"
            ? `&from=${timeframe.from}&to=${timeframe.to}`
            : `&timeframe=${timeframe}`;
        const response = await client.get(
          `/getBuildingBookings?roomId=${roomId}${timeFilterString}&status=${status}`,
        );
        return processAdminBookings(response);
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: bookingEnabled,
  });
};
export const useCompanyBookings = (
  {
    roomId = "",
    timeframe = "",
    status = "",
    companyId,
  }: UseAdminBookingsProps,
  bookingEnabled = false,
): UseQueryResult<BookingAdminEvent[]> => {
  return useBuildingQuery({
    queryKey: ["company-bookings", companyId, roomId, timeframe, status],
    queryFn: async () => {
      try {
        const timeFilterString =
          typeof timeframe === "object"
            ? `&from=${timeframe.from}&to=${timeframe.to}`
            : `&timeframe=${timeframe}`;
        const response = await client.get(
          `/getCompanyBookings?companyId=${companyId}&roomId=${roomId}${timeFilterString}&status=${status}`,
        );
        return processAdminBookings(response);
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: bookingEnabled && !!companyId,
  });
};

interface UseUserBookingsProps {
  companyId?: string;
  userId: string;
}

export interface UserBookings {
  upcoming: MeetingRoomBooking[];
  previous: MeetingRoomBooking[];
}

const partitionUserBookings = (
  bookings: MeetingRoomBooking[],
): UserBookings => {
  const upcoming: MeetingRoomBooking[] = [];
  const previous: MeetingRoomBooking[] = [];
  for (const booking of bookings) {
    (booking.showAsUpcoming ? upcoming : previous).push(booking);
  }
  return { upcoming, previous };
};

export const useUserBookings = (
  { companyId, userId }: UseUserBookingsProps,
  bookingEnabled = false,
): UseQueryResult<UserBookings> => {
  return useBuildingQuery({
    queryKey: ["user-bookings", companyId, userId],
    queryFn: async (): Promise<MeetingRoomBooking[]> => {
      try {
        const response = await client.get(
          `/getUserBookings?companyId=${companyId}&userId=${userId}`,
        );
        if (!Array.isArray(response.data.data.bookingDetails))
          throw new Error("Response is not an array");
        return response.data.data.bookingDetails.map(
          (booking: MeetingRoomBooking) => ({
            ...booking,
            event: {
              ...booking.event,
              start: firebaseTimestampToDate(
                booking.event.start as unknown as Timestamp,
              ),
              end: firebaseTimestampToDate(
                booking.event.end as unknown as Timestamp,
              ),
            },
            createdTime: firebaseTimestampToDate(
              booking.createdTime as unknown as Timestamp,
            ),
            lastEditTime: firebaseTimestampToDate(
              booking.lastEditTime as unknown as Timestamp,
            ),
          }),
        );
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    select: partitionUserBookings,
    enabled: bookingEnabled && !!companyId && !!userId,
  });
};

interface CreateMeetingRoomBookingBody {
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userLanguage: string;
  companyId: string;
  companyName: string;
  roomName: string;
  roomId: string;
  meetingFoodUrl: string;
  invoiceReference?: string | MultiFieldInvoiceReference;
  notes?: string;
  participantsCount?: number;
  eventDetails: {
    /**
     * Same as "meeting title"
     */
    summary: string;
    start: Date | string;
    end: Date | string;
  };
  welcomeSnackEnabled?: boolean;
}
export const createMeetingRoomBooking = async (
  props: CreateMeetingRoomBookingBody,
) => {
  return await client.post("/createMeetingRoomBooking", {
    ...props,
  });
};

interface CancelMeetingRoomBookingBody {
  id: string;
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLanguage: string;
  meetingFoodUrl: string;
  welcomeSnackEnabled?: boolean;
}
export const cancelMeetingRoomBooking = async (
  props: CancelMeetingRoomBookingBody,
) => {
  return await client.post("/cancelMeetingRoomBooking", {
    ...props,
    userLanguage: props.userLanguage || "no",
  });
};

interface EditMeetingRoomBookingBody {
  id: string;
  roomId: string;
  userId: string;
  companyId: string;
  meetingTitle: string;
  newStartTime: string;
  newEndTime: string;
  newMeetingTitle?: string;
  updatedByUser: { userId: string; name: string; email: string };
  userLanguage?: string;
  meetingFoodUrl?: string;
  welcomeSnackEnabled: boolean;
}
export const editMeetingRoomBooking = async (
  props: EditMeetingRoomBookingBody,
) => {
  return await client.put("/editMeetingRoomBooking", {
    ...props,
  });
};

export const createMeetingRoom = async (data: MeetingRoom) => {
  return await client.post("/createMeetingRoom", {
    ...data,
  });
};

export const updateMeetingRoom = async (data: MeetingRoom) => {
  return await client.patch("/updateMeetingRoom", {
    ...data,
  });
};

interface CreateBookingBuildingBody {
  buildingId: string;
  buildingName: string;
  description?: string;
  floors?: number;
  floorNames?: string[];
  address?: {
    addressLines: string[];
    regionCode: string;
    postalCode: string;
    administrativeArea: string;
    locality: string;
  };
}

export const createBookingBuilding = async (
  data: CreateBookingBuildingBody,
) => {
  return await client.post("/createBookingBuilding", data);
};

export type BookingBuildingResponse = {
  buildingId: string;
  buildingName: string;
  description: string;
  floors: number;
  floorNames?: string[];
  address?: {
    addressLines?: string[];
    regionCode?: string;
    postalCode?: string;
    administrativeArea?: string;
    locality?: string;
  };
};

export const getBookingBuilding = async (
  buildingId: string,
): Promise<BookingBuildingResponse | null> => {
  const response = await client.get("/getBookingBuilding", {
    params: { buildingId },
    validateStatus: (status) => status === 200 || status === 404,
  });
  if (response.status === 404) return null;
  return response.data.data as BookingBuildingResponse;
};

export const useBookingBuilding = (enabled = true) => {
  return useBuildingQuery({
    queryKey: ["bookingBuilding"],
    queryFn: () => getBookingBuilding(getBuildingId()),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    retry: false,
    enabled,
  });
};
